import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ThreeCanvasComponent } from 'src/app/core/ctc/canvas/canvas.component';
import {
  BaseModel,
  PartModel,
} from '../../layout/layouts/workspace-layout/commons/services/project-controller/project-controller';
import { Box3, Color, Mesh, Object3D, Texture, Vector3 } from 'three';
import { ActionData, MaterialType, MeshData, PresetData, StyleData } from './canvas-controller';
import { StickerListControllerService } from '../../layout/layouts/workspace-layout/commons/services/sticker-list-controller/sticker-list-controller.service';
import { StickerData } from '../../layout/layouts/workspace-layout/commons/services/sticker-list-controller/sticker-list-controller';
import {
  SettingInfo,
  StateManager,
  StickerInfo,
} from '../../layout/layouts/workspace-layout/commons/class/state-manager';

@Injectable({
  providedIn: 'root',
})
export class CanvasControllerService {
  private _canvas: ThreeCanvasComponent | undefined;
  private _canvas$: BehaviorSubject<ThreeCanvasComponent | undefined> = new BehaviorSubject<
    ThreeCanvasComponent | undefined
  >(undefined);

  private _isFirstRendered: boolean = false;
  private _isFirstRendered$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public stateManager: StateManager = new StateManager();

  public meshes: Map<string, MeshData> = new Map<string, MeshData>();
  public materials: Map<string, MaterialType> = new Map<string, MaterialType>();
  public styleMap: Map<string, StyleData> = new Map<string, StyleData>();
  public actionMap: Map<string, ActionData> = new Map<string, ActionData>();

  public sceneSize: { width: number; height: number; depth: number } = {
    width: 0,
    height: 0,
    depth: 0,
  };

  public ratio: number = 1;
  public baseModelWidth: number = 1;

  constructor(private stickerListController: StickerListControllerService) {}

  //--------------------------------------------------------------------------------
  // @ Accessors
  //--------------------------------------------------------------------------------

  get canvas(): ThreeCanvasComponent | undefined {
    return this._canvas;
  }

  set canvas(canvas: ThreeCanvasComponent | undefined) {
    this._canvas = canvas;
    this._canvas$.next(canvas);
  }

  get canvas$(): BehaviorSubject<ThreeCanvasComponent | undefined> {
    return this._canvas$;
  }

  get isFirstRendered(): boolean {
    return this._isFirstRendered;
  }

  set isFirstRendered(value: boolean) {
    this._isFirstRendered = value;
    this._isFirstRendered$.next(value);
  }

  get isFirstRendered$(): BehaviorSubject<boolean> {
    return this._isFirstRendered$;
  }

  //--------------------------------------------------------------------------------
  // @ Public methods
  //--------------------------------------------------------------------------------

  public initCavas(canvas: ThreeCanvasComponent): void {
    if (this._canvas) return;
    this.canvas = canvas;
  }

  public setMeshes(type: string, meshes: Map<string, Mesh[]>): void {
    const meshData: MeshData = {};
    meshes.forEach((mesh, category) => {
      meshData[category] = mesh;
    });
    console.log(type);
    this.meshes.set(type, meshData);
  }

  public getObjectRatio(object: Object3D, projectWidth: number): void {
    const size = new Vector3();
    const boundingBox = new Box3().setFromObject(object);
    boundingBox.getSize(size);
    const width = size.z;
    this.ratio = width / projectWidth;
  }

  public lookAt(
    position: { x?: number; y?: number; z?: number },
    target: { x?: number; y?: number; z?: number },
  ): void {
    if (!position && !target) return;
    this.canvas?.lookAtControls(target, position, true);
  }

  public firstCameraSet(): void {
    if (!this._canvas) return;
    this._canvas.firstCameraSet();
  }

  /**
   * 각도별 촬영을 실행합니다.
   * @returns 각도별 base64 이미지 데이터
   */
  public async captureAllAngles(): Promise<{
    front: string;
    back: string;
    top: string;
    left: string;
  }> {
    if (!this._canvas) {
      throw new Error('캔버스가 초기화되지 않았습니다.');
    }
    return await this._canvas.captureAllAngles();
  }

  public async addModel(
    model: BaseModel | PartModel | undefined,
    isBase?: boolean,
  ): Promise<Object3D | undefined> {
    if (!this.canvas || !model) return Promise.resolve(undefined);
    const object = await this.canvas?.addObfuscatedFile(
      model._d,
      model._f,
      model._o,
      model._u,
      isBase,
    );
    if (!object) return Promise.resolve(undefined);
    this.extractMeshMaterials(model, object);
    this.hideBaseModelByCategory(model.category);
    this.setActionData(model);

    this.sceneSize = object.userData['size'] || {
      width: 0,
      height: 0,
      depth: 0,
    };

    if (isBase) this.isFirstRendered = true;

    console.log(this.meshes);
    return object;
  }

  public removeModel(model: BaseModel | PartModel | undefined): void {
    if (!this.canvas || !model) return;
    const data = this.meshes.get(`${model.category}_${model.title}`);
    if (!data) return;
    Object.keys(data).forEach((category) =>
      data[category].forEach((mesh) => this.objectDispose(mesh)),
    );
    this.meshes.delete(model.title);
    this.visibleBaseModelByCategory(model.category);
    this.deleteActionData(model);
  }

  public allMaterialColorInit(isNotSave?: boolean): void {
    const styles: Array<SettingInfo> = [];
    this.materials.forEach((material, name) => {
      const color = (material as any).originData?.color;
      if (!color) return;
      this.canvas?.materialsColorChange(material, color);
      const stateStyle: SettingInfo = {
        material: name,
        style: {
          type: 'color',
          value: color,
        },
        isMatte: false,
      };

      styles.push(stateStyle);
    });

    if (!isNotSave) this.stateManager.setSettings(styles);
  }

  public materialColorInit(materialNames: string[], isNotSave?: boolean): void {
    const styles: Array<SettingInfo> = [];
    materialNames.forEach((name) => {
      const color = (this.materials.get(name) as any).originData?.color;
      if (!color) return;
      this.canvas?.materialsColorChange(this.materials.get(name), color);
      const stateStyle: SettingInfo = {
        material: name,
        style: {
          type: 'color',
          value: color,
        },
        isMatte: false,
      };

      styles.push(stateStyle);
    });

    if (!isNotSave) this.stateManager.setSettings(styles);
  }

  public materialsImageChange(materialNames: string[], image: string, isNotSave?: boolean): void {
    this.canvas?.materialsImageChange(
      materialNames.map((name) => this.materials.get(name)),
      image,
    );
  }

  public materialsColorChange(materialNames: string[], color: string, isNotSave?: boolean): void {
    materialNames.forEach((name) => {
      this.canvas?.materialsColorChange(this.materials.get(name), color);
      const style = this.styleMap.get(name);
      if (!style) this.styleMap.set(name, { type: 'color', value: color });
      else style.value = color;
    });

    const styles: Array<SettingInfo> = Array.from(this.styleMap.entries()).map(([name, style]) => {
      const stateStyle: SettingInfo = {
        material: name,
        style: {
          type: style.type || 'image',
          value: style.value || '',
        },
        isMatte: style.isMatte || false,
      };

      return stateStyle;
    });

    if (!isNotSave) this.stateManager.setSettings(styles);
  }

  public materialsMatteChange(
    materialNames: string[],
    isMatte: boolean,
    isNotSave?: boolean,
  ): void {
    const states: Array<SettingInfo> = [];
    materialNames.forEach((name) => {
      this.canvas?.setMatteMaterial(this.materials.get(name), isMatte);
      const style = this.styleMap.get(name);
      if (!style) this.styleMap.set(name, { isMatte: isMatte });
      else style.isMatte = isMatte;

      const stateStyle: SettingInfo = {
        material: name,
        style: {
          type: 'image',
          value: 'none',
        },
        isMatte: isMatte,
      };

      states.push(stateStyle);
    });

    if (!isNotSave) this.stateManager.setSettings(states);
  }

  public clear(): void {
    this.canvas?.clear();
  }

  //--------------------------------------------------------------------------------
  // @ Private methods
  //--------------------------------------------------------------------------------

  // TODO: 오브젝트 컨트롤 관련 동작 분리

  private extractMeshMaterials(model: BaseModel | PartModel, object: Object3D): void {
    const modelName = model.category == 'base' ? 'base' : `${model.category}_${model.title}`;
    object.traverse((child) => {
      if (!(child instanceof Mesh)) return;
      const originalData = {
        name: child.name,
        category: this.extractCategoryFromName(child.name),
        rotation: child.rotation,
        position: child.position,
      };
      child.userData['originalData'] = originalData;
      const category = this.extractCategoryFromName(child.name);
      if (!this.meshes.has(modelName)) this.meshes.set(modelName, {});

      const name = Array.from(this.actionMap.keys()).find((key) => child.name.includes(key));
      if (name) {
        const actionData = this.actionMap.get(name);
        const moveData = {
          x: (actionData?.move?.x || 0) * this.ratio,
          y: (actionData?.move?.y || 0) * this.ratio,
          z: (actionData?.move?.z || 0) * this.ratio,
        };

        const rotateData = {
          x: (actionData?.rotate?.x || 0) * (Math.PI / 180),
          y: (actionData?.rotate?.y || 0) * (Math.PI / 180),
          z: (actionData?.rotate?.z || 0) * (Math.PI / 180),
        };
        this.canvas?.selecteMeshAction(name, moveData, rotateData);
      }
      const data = this.meshes.get(modelName);
      if (!data) return;
      if (!data.hasOwnProperty(category)) data[category] = [];
      data[category].push(child);

      const material: MaterialType | undefined = child.material;
      if (!material) return;
      const originalMaterial = {
        name: material.name,
        color: (material as any).color,
        map: (material as any).map,
      };
      material.userData['originalMaterial'] = originalMaterial;
      const materialName = this.extractMaterialNames(material);
      this.materials.set(materialName, material);
      const color = (material as any).color;
      if (!color) return;
      if (!this.styleMap.has(materialName))
        this.styleMap.set(materialName, { type: 'color', value: `#${color.getHexString()}` });
      else (material as any).color = new Color(this.styleMap.get(materialName)?.value);
      material.needsUpdate = true;
    });
  }

  private extractCategoryFromName(name: string): string {
    const result = name.match(/\((.*?)\)/);
    if (!result) return 'none-category';
    return result[1].replace('_', ' ');
  }

  private extractMaterialNames(material: MaterialType): string {
    const isDisabled = material.name[0] === '0';
    const isChangeColor = material.name.split('.')[0].includes('C');
    const isAttachSticker = material.name.split('.')[0].includes('I');

    if (isDisabled) material.userData['disabled'] = true;
    if (isChangeColor) material.userData['changeColor'] = true;
    if (isAttachSticker) material.userData['attachSticker'] = true;
    return material.name.replace(/^\d+_([A-Z]+\.)?/, '');
  }

  private visibleBaseModelByCategory(category: string | undefined): void {
    if (!category) return;
    const baseData = this.meshes.get('base');
    if (!baseData || !baseData[category]) return;
    baseData[category].forEach((mesh) => {
      mesh.visible = true;
      const material = mesh.material;
      const color = (material as any).color;
      const materialName = this.extractMaterialNames(mesh.material as MaterialType);
      if (!this.styleMap.has(materialName))
        this.styleMap.set(materialName, { type: 'color', value: `#${color.getHexString()}` });
      else (material as any).color = new Color(this.styleMap.get(materialName)?.value);
      material.needsUpdate = true;
    });
  }

  private hideBaseModelByCategory(category: string | undefined): void {
    if (!category) return;
    const baseData = this.meshes.get('base');
    if (!baseData || !baseData[category]) return;
    baseData[category].forEach((mesh) => (mesh.visible = false));
  }

  private objectDispose(object: Object3D): void {
    object.traverse((child) => {
      if (child instanceof Mesh) {
        if (child.geometry) {
          child.geometry.dispose();
        }

        if (Array.isArray(child.material)) {
          child.material.forEach((mat) => mat.dispose());
        } else if (child.material) {
          child.material.dispose();
        }

        if (child.material && 'map' in child.material && child.material.map) {
          child.material.map.dispose();
        }
        if (child.material && 'uniforms' in child.material) {
          for (const key in child.material.uniforms) {
            if (child.material.uniforms[key].value instanceof Texture) {
              child.material.uniforms[key].value.dispose();
            }
          }
        }
      }

      child.userData = {};

      if ('removeEventListener' in child) {
        (child as any).removeEventListener();
      }
    });

    object.parent?.remove(object);

    (object as any) = null;
  }

  // TODO: 액션 관련 동작 분리

  private setActionData(part: BaseModel | PartModel): void {
    if (!part.actions || part.actions.length === 0) return;
    part.actions.forEach((action) => {
      action.target.forEach((target) => {
        if (!this.actionMap.has(target))
          this.actionMap.set(target, {
            move: { x: 0, y: 0, z: 0 },
            rotate: { x: 0, y: 0, z: 0 },
          });
        const actionData = this.actionMap.get(target);
        let moveData = { x: 0, y: 0, z: 0 };
        let rotationData = { x: 0, y: 0, z: 0 };
        if (actionData && actionData.move) {
          actionData.move.x += action?.move?.x || 0;
          actionData.move.y += action?.move?.y || 0;
          actionData.move.z += action?.move?.z || 0;

          moveData = {
            x: actionData.move.x * this.ratio,
            y: actionData.move.y * this.ratio,
            z: actionData.move.z * this.ratio,
          };
        }

        if (actionData && actionData.rotate) {
          actionData.rotate.x += action?.rotation?.x || 0;
          actionData.rotate.y += action?.rotation?.y || 0;
          actionData.rotate.z += action?.rotation?.z || 0;

          rotationData = {
            x: actionData.rotate.x * (Math.PI / 180),
            y: actionData.rotate.y * (Math.PI / 180),
            z: actionData.rotate.z * (Math.PI / 180),
          };
        }
        this.actionMap.set(target, { move: moveData, rotate: rotationData });
        this.canvas?.selecteMeshAction(target, moveData, rotationData);
      });
    });
  }

  private deleteActionData(part: BaseModel | PartModel): void {
    if (!part.actions || part.actions.length === 0) return;
    part.actions.forEach((action) => {
      action.target.forEach((target) => {
        if (!this.actionMap.has(target))
          this.actionMap.set(target, { move: { x: 0, y: 0, z: 0 }, rotate: { x: 0, y: 0, z: 0 } });
        const actionData = this.actionMap.get(target);
        let moveData = { x: 0, y: 0, z: 0 };
        let rotationData = { x: 0, y: 0, z: 0 };
        if (actionData && actionData.move) {
          actionData.move.x -= action?.move?.x || 0;
          actionData.move.y -= action?.move?.y || 0;
          actionData.move.z -= action?.move?.z || 0;

          moveData = {
            x: actionData.move.x * this.ratio,
            y: actionData.move.y * this.ratio,
            z: actionData.move.z * this.ratio,
          };
        }

        if (actionData && actionData.rotate) {
          actionData.rotate.x -= action?.rotation?.x || 0;
          actionData.rotate.y -= action?.rotation?.y || 0;
          actionData.rotate.z -= action?.rotation?.z || 0;
          rotationData = {
            x: actionData.rotate.x * (Math.PI / 180),
            y: actionData.rotate.y * (Math.PI / 180),
            z: actionData.rotate.z * (Math.PI / 180),
          };
        }
        this.actionMap.set(target, { move: { x: 0, y: 0, z: 0 }, rotate: { x: 0, y: 0, z: 0 } });
        this.canvas?.selecteMeshAction(target, moveData, rotationData);
      });
    });
  }

  // TODO: 스티커 관련 동작 분리

  public createStickerView(url: string, type: 'livery' | 'sticker'): Promise<Mesh | undefined> {
    if (!this.canvas) return new Promise((resolve) => resolve(undefined));
    this.canvas.onRaycast();
    this.canvas.holdCameraControls();
    this.canvas.lookAtDirection('left', false);
    return this.canvas.addStickerViewerUrl(
      url,
      this.stickerListController.getRenderOreder(),
      type == 'livery' ? 3 : 1,
    );
  }

  public cancelStickerView(): void {
    if (!this.canvas) return;
    this.canvas.offRaycast();
    this.canvas.releaseCameraControls();
    this.canvas.removeStickerViewer();
  }

  public channgeScaleStickerView(scale: number): void {
    if (!this.canvas) return;
    this.canvas.stickerViewerScale(scale);
  }

  public channgeRotateStickerView(rotate: number): void {
    if (!this.canvas) return;
    this.canvas.stickerViewerRotation(rotate * (Math.PI / 180));
  }

  public changeCameraViewPosition(positionName: 'top' | 'left' | 'right' | 'front' | 'back'): void {
    if (!this.canvas) return;
    this.canvas.lookAtDirection(positionName, false);
  }

  public attachSticker(sticker: StickerData, isDualSide: boolean): void {
    if (!this.canvas) return;

    const stickerInfo = isDualSide
      ? this.canvas.dualSidedStickerAttach(
          this.stickerListController.getRenderOreder(),
          sticker.title,
        )
      : this.canvas.oneSideAttachSticker(
          this.stickerListController.getRenderOreder(),
          sticker.title,
        );
    this.stickerListController.addStickerMeshes(stickerInfo.stickerMeshes);
    this.stickerListController.addAttachSticker(sticker);

    const stickerData: StickerInfo = {
      attachMeshNames: stickerInfo.attachMesh.map((mesh: any) => mesh.name),
      stickerUrl: sticker.image,
      position: {
        x: stickerInfo.position.x,
        y: stickerInfo.position.y,
        z: stickerInfo.position.z,
      },
      rotation: {
        x: stickerInfo.rotation.x,
        y: stickerInfo.rotation.y,
        z: stickerInfo.rotation.z,
      },
      renderOrder: stickerInfo.renderOrder,
      scale: stickerInfo.scale.x,
      scaleRatio: stickerInfo.scaleRatio,
      isDualSided: stickerInfo.isDualSided,
    };
    this.stateManager.addSticker(stickerData);
    this.cancelStickerView();
  }

  public clearAllStickers(): void {
    if (!this.canvas) return;

    // 1) ThreeCanvasComponent 쪽에서 실제 메쉬 제거
    this.canvas.clearAllDecals();

    // 2) StickerListControllerService 로컬 리스트 초기화
    //    (메서드가 없다면 추가해주세요)
    this.stickerListController.clearAll();
  }

  public changeStickerDeth(meshs: Array<Mesh<any>>, deth: number): void {
    if (!this.canvas) return;
    meshs.forEach((mesh) => this.canvas?.changeStickerDepth(mesh, deth));
  }

  public removeSticker(meshes: Array<Mesh>): void {
    meshes.forEach((mesh) => {
      if (!this.canvas) return;
      this.canvas.removeObject(mesh);
    });
  }

  public onCutMode(index: number): void {
    if (!this.canvas) return;
    this.canvas.onDeleteStickerMode(index, this.stickerListController.stickerMeshes[index][0].name);
  }

  public offCutMode(): void {
    if (!this.canvas) return;
    this.canvas.offDeleteStickerMode();
  }

  public undo(): void {
    if (!this.canvas) return;
    const undoData = this.stateManager.undo();
  }

  public redo(): void {
    if (!this.canvas) return;
    const redoData = this.stateManager.redo();
  }

  public lockCamera(isCameraLocked: boolean): void {
    if (!this.canvas) return;
    if (isCameraLocked) this.changeCameraViewPosition('front');
    this.canvas.setCameraLocked(isCameraLocked);
  }
}
